// The page side of the rotation (BUGS.md T55, the page of T54; module 04 B2220, Cobrain 0.13). Mike, 6 Oct 2026 about
// 20:10 CEST by pop-up, "Rotation until decided (Recommended)": "Places kept daily for the longest-waiting; each comes back
// with what changed and asks: today, a date, hand on, or close. No limit on how often." The preview he chose draws a card
// back by the rotation as "(back) Genius Sports InPlay   not in front of you since <date>" with its four choices under it,
// and under the list one line, "Still open, not today: n, longest waiting: d days", which opens the full open list.
//
// What the read gives (apps/api/src/routes/triage-read.ts, rank/item-words.ts): each card's `back`, null or {since, days},
// since being the person's own day it was last in front of them ('YYYY-MM-DD', null when never); and the list's `stillOpen`,
// null or {count, longestDays, items longest first}. POST /api/triage/visit {passId, via} says the list was drawn for the
// person, on load and on focus, never on the page's own refresh (migration 226), so the rotation knows what they saw.
//
// The shapes live here rather than in api.ts, which is another track's file (infra/agent-guard/OWNERS).
import { viewFetch as fetch } from './view-as.js';
import { dayMonth } from './day.js';
import type { PassItem } from './api.js';

/** A card back by the rotation: the day it was last in front of the person (null: never) and the working days it waited. */
export interface Back { since: string | null; days: number; changed?: Changed | null }

/**
 * Module 04 B2421 (Mike by pop-up, 8 Oct 2026 about 13:58 CEST, "All as recommended", its (a): "a line under a card saying what
 * arrived since you last saw it"): what arrived on the card since it was last in front of the person ('seen') or, never, since it
 * came in ('arose'), as the read words it: how many, and a line a message, newest first (the last line "n more" when there are).
 */
export interface Changed { from: 'seen' | 'arose'; count: number; lines: string[] }

function changedOf(v: unknown): Changed | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as { from?: unknown; count?: unknown; lines?: unknown };
  if (typeof o.count !== 'number' || !Number.isFinite(o.count) || (o.from !== 'seen' && o.from !== 'arose')) return null;
  return { from: o.from, count: o.count, lines: Array.isArray(o.lines) ? o.lines.filter((x): x is string => typeof x === 'string') : [] };
}

/**
 * The line under a card back by the rotation. On the list, the newest message and how many besides ("Since then: Conor Rafferty:
 * Re: Pax list (2026-10-02), and 2 more"); on the item page (`full`), every line the read gives. Null when the read said nothing.
 */
export function changedWords(b: Back | null, full = false): string | null {
  const c = b?.changed;
  if (!c) return null;
  const lead = c.from === 'seen' ? 'Since then' : 'Since it came in';
  if (c.count === 0) return `${lead}: nothing new.`;
  const named = c.lines.filter((l) => !/^\d+ more$/.test(l));
  if (full) return `${lead}: ${c.lines.join('; ')}.`;
  const rest = c.count - 1;
  return `${lead}: ${named[0] ?? `${c.count} message${c.count === 1 ? '' : 's'}`}${named[0] && rest > 0 ? `, and ${rest} more` : ''}.`;
}

/** GET /api/triage's `stillOpen`: what is open and not on today's list, longest waiting first (at most 200 named). */
export interface StillOpen { count: number; longestDays: number; items: StillOpenItem[] }
export interface StillOpenItem { kind: string; id: string; headline: string; waitingDays: number; lastOn: string | null }

/** The rotation's mark on a card, or null: absent from an older server and on every card not back. */
export function backOf(item: PassItem): Back | null {
  const b = (item as PassItem & { back?: unknown }).back;
  if (!b || typeof b !== 'object') return null;
  const o = b as { since?: unknown; days?: unknown; changed?: unknown };
  if (typeof o.days !== 'number' || !Number.isFinite(o.days)) return null;
  const changed = changedOf(o.changed);
  return { since: typeof o.since === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(o.since) ? o.since : null, days: o.days, ...(changed ? { changed } : {}) };
}

export const isBack = (item: PassItem): boolean => backOf(item) !== null;

/** A day as the read writes it ('2026-10-01'), in the card's words: "1 Oct". The day is the person's own, so it is drawn as written. */
export function dayWords(on: string): string {
  return dayMonth(new Date(`${on}T00:00:00Z`), 'utc');
}

/**
 * The line beside a card back by the rotation, in the preview's words: "not in front of you since 1 Oct". A card never in
 * front of the person has no such day; "not in front of you yet" is a build choice put to Mike (module 04 B-entry of T55).
 */
export function sinceWords(since: string | null): string {
  return since ? `not in front of you since ${dayWords(since)}` : 'not in front of you yet';
}

/** The four choices of a card back by the rotation, in the order the preview draws them. */
export const BACK_CHOICES = ['Today', 'A date', 'Hand on', 'Close'] as const;
export type BackChoice = (typeof BACK_CHOICES)[number];

/** The kinds of option a card offers that hand it on (T55: "Ask to take or Leave it with"), in the card's order. */
export const HAND_ON_KINDS: ReadonlySet<string> = new Set(['ask_to_take', 'leave_with']);

/** The read's `stillOpen`, checked: null from an older server, a pass written before 0.13, or a shape the page cannot draw. */
export function stillOpenOf(view: unknown): StillOpen | null {
  const s = (view as { stillOpen?: unknown } | null)?.stillOpen;
  if (!s || typeof s !== 'object') return null;
  const o = s as { count?: unknown; longestDays?: unknown; items?: unknown };
  if (typeof o.count !== 'number' || typeof o.longestDays !== 'number') return null;
  const items = Array.isArray(o.items) ? o.items.filter((x): x is StillOpenItem => !!x && typeof x === 'object' && typeof (x as { id?: unknown }).id === 'string') : [];
  return { count: o.count, longestDays: o.longestDays, items: items.map((x) => ({ kind: String(x.kind), id: x.id, headline: String(x.headline ?? ''), waitingDays: Number(x.waitingDays ?? 0), lastOn: typeof x.lastOn === 'string' ? x.lastOn : null })) };
}

/**
 * The stores' still-open counts as one (the blended page, D12, D43): the counts added, the longest wait the longest of
 * them, the items longest waiting first, each with the store whose read named it. Null when no store's read carries one.
 */
export function mergeStillOpen(per: ReadonlyArray<{ store: string; stillOpen: StillOpen | null }>): (Omit<StillOpen, 'items'> & { items: Array<StillOpenItem & { store: string }> }) | null {
  const got = per.filter((p): p is { store: string; stillOpen: StillOpen } => p.stillOpen !== null);
  if (got.length === 0) return null;
  const items = got.flatMap((p) => p.stillOpen.items.map((i) => ({ ...i, store: p.store })));
  items.sort((a, b) => b.waitingDays - a.waitingDays);
  return { count: got.reduce((n, p) => n + p.stillOpen.count, 0), longestDays: Math.max(...got.map((p) => p.stillOpen.longestDays)), items };
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** How the list came to be drawn for the person: the page opened, or they came back to it. The page's own refresh is neither. */
export type VisitVia = 'load' | 'focus';

/**
 * POST /api/triage/visit: the list was drawn for the person from this pass. Nothing on the page waits for it or shows it, and a
 * failure is dropped without a word, as an open is (api.ts triage.opened). A pass id that is not one (no pass yet) sends nothing.
 */
export function recordVisit(store: string, passId: string | null, via: VisitVia): boolean {
  if (!passId || !UUID.test(passId)) return false;
  void fetch('/api/triage/visit', { method: 'POST', credentials: 'same-origin', keepalive: true, headers: { 'content-type': 'application/json' }, body: JSON.stringify({ store, passId, via }) }).catch(() => undefined);
  return true;
}
